/**
 * Transaction matching engine.
 *
 * Matches incoming parsed transactions against existing transactions using
 * cascading rules: ref match → exact → date tolerance → amount tolerance.
 *
 * Leverages parser-extracted fields (extractedName, upiId, referenceNumber)
 * rather than re-parsing raw descriptions.
 *
 * Environment-agnostic: runs in WebWorker or server.
 */

import type { Conflict, ExistingTransaction, Match, ParsedTransaction } from "@paisa/types"
import { getMatchName, isSameMerchant } from "./normalise"

const DEFAULT_DATE_TOLERANCE = 2 // days
const DEFAULT_AMOUNT_TOLERANCE = 0.02 // 2%

interface MatchConfig {
  dateTolerance: number
  amountTolerance: number
}

/** Date as real epoch days. The previous `y*365 + m*30 + d` was not a true day
 *  count, so date-tolerance comparisons broke across month/year boundaries
 *  (Jan 31 → Feb 1 read as 0 days apart; Dec 31 → Jan 1 as 5) (#70 C1). */
function toDays(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number)
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000)
}

/** Check if two amounts are within a tolerance fraction */
function amountsClose(a: number, b: number, tolerance: number): boolean {
  if (a === 0 || b === 0) return false
  return Math.abs(a - b) / Math.max(a, b) <= tolerance
}

/**
 * Check if two transactions describe the same entity based on parsed fields.
 *
 * Priority:
 * 1. UPI ID match (strongest after reference number)
 * 2. extractedName / merchantName comparison
 * 3. Raw description fallback
 */
function namesSimilar(inc: ParsedTransaction, ex: ExistingTransaction): boolean {
  // UPI ID match — very strong signal
  if (inc.upiId && ex.upiId && inc.upiId === ex.upiId) return true

  // Compare best available names
  const incName = getMatchName(inc.extractedName, inc.description)
  const exName = getMatchName(ex.extractedName ?? ex.merchantName, ex.description)

  return isSameMerchant(incName, exName)
}

/**
 * Match incoming transactions against existing ones.
 *
 * Each incoming transaction is matched to at most one existing transaction.
 * Once an existing transaction is claimed, it can't be matched again.
 */
export function matchTransactions(
  existing: ExistingTransaction[],
  incoming: ParsedTransaction[],
  config?: Partial<MatchConfig>,
): { matched: Match[]; conflicts: Conflict[]; unmatchedIndices: number[] } {
  const dateTolerance = config?.dateTolerance ?? DEFAULT_DATE_TOLERANCE
  const amountTolerance = config?.amountTolerance ?? DEFAULT_AMOUNT_TOLERANCE

  const claimedExisting = new Set<string>()
  const claimedIncoming = new Set<number>()

  const matched: Match[] = []
  const conflicts: Conflict[] = []

  // Eligible existing transactions (skip already-duplicate/ignored)
  const eligible = existing.filter((t) => !t.isDuplicate && !t.isIgnored)

  // ── Pass 1: Same-file identity — description + amount + type + date all identical.
  // Fastest and most precise: catches every re-import of the same statement.
  for (let i = 0; i < incoming.length; i++) {
    if (claimedIncoming.has(i)) continue
    const inc = incoming[i]

    for (const ex of eligible) {
      if (claimedExisting.has(ex.id)) continue
      if (
        inc.description === ex.description &&
        inc.amount === ex.amount &&
        inc.type === ex.type &&
        inc.date === ex.date
      ) {
        matched.push({
          existing: ex,
          incoming: inc,
          incomingIndex: i,
          confidence: "exact",
          reason: "Same row: identical description, amount, type, and date",
        })
        claimedExisting.add(ex.id)
        claimedIncoming.add(i)
        break
      }
    }
  }

  // ── Pass 2: Reference number exact match (UPI ref, NEFT ref — strongest cross-source signal)
  for (let i = 0; i < incoming.length; i++) {
    if (claimedIncoming.has(i)) continue
    const inc = incoming[i]
    if (!inc.referenceNumber) continue

    for (const ex of eligible) {
      if (claimedExisting.has(ex.id)) continue
      if (
        ex.referenceNumber === inc.referenceNumber &&
        inc.type === ex.type &&
        inc.amount === ex.amount
      ) {
        matched.push({
          existing: ex,
          incoming: inc,
          incomingIndex: i,
          confidence: "exact",
          reason: `Reference number match: ${inc.referenceNumber}`,
        })
        claimedExisting.add(ex.id)
        claimedIncoming.add(i)
        break
      }
    }
  }

  // ── Pass 3: Exact match — same amount, type, date, similar name
  for (let i = 0; i < incoming.length; i++) {
    if (claimedIncoming.has(i)) continue
    const inc = incoming[i]

    for (const ex of eligible) {
      if (claimedExisting.has(ex.id)) continue
      if (
        inc.amount === ex.amount &&
        inc.type === ex.type &&
        inc.date === ex.date &&
        namesSimilar(inc, ex)
      ) {
        matched.push({
          existing: ex,
          incoming: inc,
          incomingIndex: i,
          confidence: "exact",
          reason: `Exact match: ${inc.amount} on ${inc.date}`,
        })
        claimedExisting.add(ex.id)
        claimedIncoming.add(i)
        break
      }
    }
  }

  // ── Pass 4: Date tolerance — same amount + type + name, date within ±N days
  for (let i = 0; i < incoming.length; i++) {
    if (claimedIncoming.has(i)) continue
    const inc = incoming[i]
    const incDays = toDays(inc.date)

    for (const ex of eligible) {
      if (claimedExisting.has(ex.id)) continue

      const daysDiff = Math.abs(incDays - toDays(ex.date))
      if (
        daysDiff > 0 &&
        daysDiff <= dateTolerance &&
        inc.amount === ex.amount &&
        inc.type === ex.type &&
        namesSimilar(inc, ex)
      ) {
        matched.push({
          existing: ex,
          incoming: inc,
          incomingIndex: i,
          confidence: "date_tolerance",
          reason: `Amount + name match, ${daysDiff} day${daysDiff > 1 ? "s" : ""} apart`,
        })
        claimedExisting.add(ex.id)
        claimedIncoming.add(i)
        break
      }
    }
  }

  // ── Pass 5: Amount tolerance — name + date close, amount differs within threshold
  // These become conflicts (flagged for review), not auto-matched
  for (let i = 0; i < incoming.length; i++) {
    if (claimedIncoming.has(i)) continue
    const inc = incoming[i]
    const incDays = toDays(inc.date)

    for (const ex of eligible) {
      if (claimedExisting.has(ex.id)) continue

      const daysDiff = Math.abs(incDays - toDays(ex.date))
      if (
        daysDiff <= dateTolerance &&
        inc.type === ex.type &&
        inc.amount !== ex.amount &&
        amountsClose(inc.amount, ex.amount, amountTolerance) &&
        namesSimilar(inc, ex)
      ) {
        const pctDiff = (
          (Math.abs(inc.amount - ex.amount) / Math.max(inc.amount, ex.amount)) *
          100
        ).toFixed(1)
        conflicts.push({
          existing: ex,
          incoming: inc,
          incomingIndex: i,
          reason: `Amount differs by ${pctDiff}% (${ex.amount} vs ${inc.amount})`,
        })
        claimedExisting.add(ex.id)
        claimedIncoming.add(i)
        break
      }
    }
  }

  // Remaining incoming are unmatched
  const unmatchedIndices: number[] = []
  for (let i = 0; i < incoming.length; i++) {
    if (!claimedIncoming.has(i)) unmatchedIndices.push(i)
  }

  return { matched, conflicts, unmatchedIndices }
}
